import http from 'axios'
import { flow, join, split, reverse, pickBy, keys, identity } from 'lodash/fp'

export const getNumbers = () => ({
  type: 'NUMBERS/GET_NUMBERS',
  payload: http({
    method: 'GET',
    url: 'api/v1/numbers',
    params: {
      limit: 1000,
    },
  }),
})

export const getNumber = serviceId => ({
  type: 'NUMBERS/GET_NUMBER',
  payload: http({
    method: 'GET',
    url: `api/v1/numbers/${serviceId}`,
  }),
  meta: {
    id: serviceId,
  },
})

export const getNumbersAnalytics = () => ({
  type: 'NUMBERS/GET_NUMBERS_ANALYTICS',
  payload: http({
    method: 'GET',
    url: 'api/v1/numbers/analytics',
    params: {
      limit: 1000,
    },
  }),
})

export const getAvailableNumbers = ({ country, type, filter, cacheKey }) => ({
  type: 'NUMBERS/GET_AVAILABLE_NUMBERS',
  payload: http({
    method: 'POST',
    url: 'api/v1/twilio/numbers/search',
    data: {
      country,
      type,
      filter: {
        ...filter,
        voiceEnabled: true,
      },
    },
  }),
  meta: {
    cacheKey,
  },
})

const getOverRides = formData => {
  // We do not destroy the data throughout the wizard so we need to manually set some values
  // to undefined as required. This depends on the form order.
  if (formData.isNewNumber) {
    return {
      existingNumberProvider: undefined,
      existingNumberAccount: undefined,
      erouHash: undefined,
    }
  }
  if (formData.isExistingNumberInActive) {
    return {
      isSmartNumber: true,
      existingNumberProvider: undefined,
      existingNumberAccount: undefined,
    }
  }
  return {
    isSmartNumber: undefined,
    erouHash: undefined,
  }
}

export const createNumber = formData => (dispatch, getState) => {
  if (formData.usagePlanAlias && formData.usagePlanAlias.startsWith('twilio-')) {
    return dispatch({
      type: 'NUMBERS/CREATE_NUMBER',
      payload: http({
        method: 'POST',
        url: 'api/v1/numbers/twilio',
        data: {
          number: formData.number,
          ban: formData.ban,
          usagePlanAlias: formData.usagePlanAlias,
          customerReference: formData.customerReference,
          endUserType: formData.endUserType,
          documents: (formData.uploadedFiles || []).map(file => file.hash),
          callForwardingNumber: formData.callForwardingNumber,
          smsForwardingEmail: formData.smsForwardingEmail,
        },
      }),
      meta: {
        yearlyValue: 10 * 12,
      },
    })
  }

  const apiPath = formData.productId.includes('1300') ? '1300' : '1800'
  const cmsKey = formData.productId.includes('1300') ? 'product1300Plans' : 'product1800Plans'

  return dispatch({
    type: 'NUMBERS/CREATE_NUMBER',
    payload: http({
      method: 'POST',
      url: `api/v1/numbers/${apiPath}`,
      data: {
        ban: formData.ban,
        number: formData.number,
        usagePlanAlias: formData.productId,
        forwardingNumber: formData.forwardingNumber,
        isNewNumber: formData.isNewNumber,
        isSmartNumber: formData.isPremiumNumber,
        existingNumberProvider: formData.existingNumberProvider,
        existingNumberAccount: formData.existingNumberAccount,
        customerReference: formData.customerReference,
        erouHash: formData.proofOfPurchase.id,
        ...getOverRides(formData),
      },
    }),
    meta: {
      yearlyValue:
        getState().cms[cmsKey].find(product => product.fields.id === formData.productId).fields
          .costPerMonth *
          12 + // Base rate
        (formData.premiumNumberMonthlyFee || 0) * 12, // Premium fee
    },
  })
}

export const updateNumber = service => ({
  type: 'NUMBERS/UPDATE_NUMBER',
  payload: http({
    method: 'PUT',
    url: `api/v1/numbers/${service.id}`,
    data: service,
  }),
})

export const linkTwilioToAapt = ({ serviceIdTwilio, serviceIdAapt }) => ({
  type: 'NUMBERS/LINK_TWILIO_TO_AAPT',
  payload: http({
    method: 'POST',
    url: `api/v1/numbers/twilio/${serviceIdTwilio}/flow-type`,
    data: {
      flowType: 'inbound',
      flowServiceId: serviceIdAapt,
    },
  }),
  meta: {
    serviceId: serviceIdTwilio,
  },
})

const transformData = formData => ({
  [formData.planType]: {
    ...formData[formData.planType],
    ...(formData.planType === 'businessContinuity'
      ? {
          openHours: `${formData[formData.planType].openHours.from}-${
            formData[formData.planType].openHours.to
          }`,
          openDays: flow(pickBy(identity), keys)(formData[formData.planType].openDays),
        }
      : {}),
  },
})

export const changeCallPlan = (serviceId, data) => dispatch => {
  const transformedData = transformData(data)
  return dispatch({
    type: 'NUMBERS/CHANGE_CALL_PLAN',
    payload: http({
      method: 'POST',
      url: `api/v1/numbers/${serviceId}/call-plan`,
      data: transformedData,
    }),
  })
}

export const changeUsagePlan = (serviceId, data) => ({
  type: 'NUMBERS/CHANGE_USAGE_PLAN',
  payload: http({
    method: 'POST',
    url: `api/v1/numbers/${serviceId}/usage-plan`,
    data,
  }),
})

export const getChangeUsagePlanFee = serviceId => ({
  type: 'NUMBERS/GET_CHANGE_USAGE_PLAN_FEE',
  payload: http({
    method: 'GET',
    url: `api/v1/numbers/${serviceId}/usage-plan/fee`,
  }),
  meta: {
    serviceId,
  },
})

export const getSuitableUsagePlans = serviceId => ({
  type: 'NUMBERS/GET_SUITABLE_USAGE_PLANS',
  payload: http({
    method: 'GET',
    url: `api/v1/numbers/${serviceId}/usage-plan`,
  }),
  meta: {
    serviceId,
  },
})

/** Transforms 30/10/1990 -> 1990-10-30 */
export const transformDate = flow(split('/'), reverse, join('-'))

export const getCallUsage = ({ numberIds, page, limit, cacheKey, dateFilter, token }) => {
  const dateTransformed = transformDate(dateFilter) || undefined
  return {
    type: 'NUMBERS/GET_CALL_USAGE',
    payload: Promise.all(
      numberIds.map(numberId =>
        http({
          method: 'GET',
          url: `api/v1/numbers/${numberId}/call-usage`,
          params: {
            offset: (page - 1) * 20,
            limit: limit || 20,
            'filter[date]': dateTransformed,
            _token: token,
          },
        })
      )
    ),
    meta: {
      cacheKey,
    },
  }
}

export const getAllCallUsage = () => ({
  type: 'NUMBERS/GET_ALL_CALL_USAGE',
  payload: http({
    method: 'GET',
    url: 'api/v1/numbers/call-usage',
    params: {
      limit: 11000,
    },
  }),
  meta: {
    cacheKey: 'all',
  },
})

export const getDisconnectionFee = serviceId => ({
  type: 'NUMBERS/GET_DISCONNECTION_FEE',
  payload: http({
    method: 'GET',
    url: `api/v1/services/${serviceId}/disconnect`,
  }),
  meta: {
    serviceId,
  },
})

export const disconnectService = (serviceId, reason) => ({
  type: 'NUMBERS/DISCONNECT_SERVICE',
  payload: http({
    method: 'POST',
    url: `api/v1/services/${serviceId}/disconnect`,
    data: {
      reason,
    },
  }),
  meta: {
    serviceId,
  },
})



// WEBPACK FOOTER //
// ./~/@pickle/pickle-shared/src/features/Numbers/Numbers.actions.js